/**
 * DIBELS8 composite score calculation service.
 * @module composite_score_service
 *
 */
const Decimal = require('decimal.js');
const cloneDeep = require('lodash.clonedeep');
const cutscores = require('dibels8-content').compositeScoreCutscores;
const {
    SECURE_SCORE_TYPE,
    REGULAR_SCORE_TYPE,
    SCORE_TYPE_PRECEDENCE,
    COMPOSITE_SCORE_MULTIPLIER,
    MEAN_BY_GRADE_PERIOD,
    MEASURE_WEIGHTS_BY_GRADE,
    MEASURE_WEIGHTS_MULTIPLIER_BY_GRADE_PERIOD,
    SCORE_OFFSET_CONSTANT_BY_GRADE_PERIOD,
    SD_BY_GRADE_PERIOD,
} = require('./calculation_constants');
const { getScoresInfoByWindow, getRawScore } = require('./lib');
const { getGatedMeasureScores, isGatingProcedureMet } = require('./gating_rules_service');
const { EQUATED_SCORES_MAP } = require('./equated_scores');

const COMPOSITE_SCORE_WITH_PAUSED_INTERP = {
    compositeScore: null,
    interpretation: 'Paused, No Interpretation',
};

/**
 * Return raw composite score cutscores.
 */
function getRawCompositeCutscores() {
    return cutscores;
}

/**
 * Round given num with precision to the hundredths place (to the second digit after decimal point).
 * Examples:
 * toHundredthsPrecision(1.444444) === 1.44
 * toHundredthsPrecision(1.445555) === 1.45
 * toHundredthsPrecision(1.454545) === 1.46
 *
 * @param {Number} num - number to round
 * @returns {Number} - rounded input number
 */
function toHundredthsPrecision(num) {
    return Math.round(Math.round(num * 1000) / 10) / 100;
}

/**
 * Return interpretation string of the composite score.
 *
 * @param {string} grade - student's grade
 * @param {string} period - time of the year
 * @param {Number} compositeScore - numeric composite score value
 * @returns {string} Interpretation string. Throws Error if got inappropriate score value.
 */
function getCompositeScoreInterpretation(grade, period, compositeScore) {
    const windowCutscoreObj = cutscores[`${grade}_${period}`];
    if (windowCutscoreObj === undefined) {
        throw Error(`No cutscores found for grade ${grade} and period ${period}`);
    }
    const thresholdObj = windowCutscoreObj.thresholds.find(threshold => (compositeScore >= threshold.scoreThreshold));
    if (thresholdObj === undefined) {
        throw Error(`Can't find interpretation for composite score ${compositeScore} in ${grade} ${period}`);
    }
    return thresholdObj.interpretation;
}

/**
 * Returns total weighted score for all measure scores.
 *
 * @param {string} grade - student's grade
 * @param {object} measureScores - The object with student's measure scores.
 */
function getTotalWeightedScore(grade, period, measureScores) {
    const completedMeasures = Object.keys(measureScores);
    return completedMeasures.reduce((cumulativeWeightedScore, measureName) => {
        const measureScoreObj = measureScores[measureName];
        const rawScore = getRawScore(measureName, measureScoreObj);
        const weight = MEASURE_WEIGHTS_BY_GRADE[grade][measureName];
        const weightMultiplierLookup = `${grade}_${period}_${measureName}`;
        const weightMultiplier = MEASURE_WEIGHTS_MULTIPLIER_BY_GRADE_PERIOD[weightMultiplierLookup] || 1;
        const weightedScore = toHundredthsPrecision(rawScore * (weight * weightMultiplier));
        return cumulativeWeightedScore + weightedScore;
    }, 0);
}


/**
 * Returns equated composite score value based on a raw composite score.
 *
 * @param {int} rawScore - raw composite score
 * @param {object} params - schoolYear, grade, period
 */
function getEquatedScore(rawScore, params) {
    try {
        const { schoolYear, grade, period } = params;
        return EQUATED_SCORES_MAP[schoolYear][grade][period][rawScore];
    } catch (error) {
        const errorMessage = `Cannot equate composite score. Not valid input: ${JSON.stringify({ rawScore, params })}`;
        console.error(errorMessage);
        throw new Error(errorMessage);
    }
}

/**
 * Returns composite score's value.
 *
 * Calculation formula:
 * compositeScore = ((totalWeightedScore - mean)/standardDeviation * 40) + scoreOffsetConstant
 * where totalWeightedScore is sum of all measures (rawScore * weight) product which is rounded with hundredths
 * all other operands are consts listed in the calculation_constants module.
 * Value of ((totalWeightedScore - mean)/standardDeviation * 40) is rounded to the ones place.
 * scorePrecision is 3 and applied only for results created in SY 24-25 (based on params.schoolYear)
 *
 * @param {object} params - Properties:
 * grade - string - student's grade;
 * period - string - Assessment time of year (BOY|MOY|EOY|SUMMER);
 * scoresType - string - type of the measure scores (regular | secure)
 * @param {object} measureScores - The object with student's measure scores.
 */
function getCompositeScoreValue(params, measureScores) {
    const totalWeightedScore = getTotalWeightedScore(params.grade, params.period, measureScores);
    const mean = MEAN_BY_GRADE_PERIOD[params.grade][params.period];
    const standardDeviation = SD_BY_GRADE_PERIOD[params.grade][params.period];
    const scoreOffsetConstant = SCORE_OFFSET_CONSTANT_BY_GRADE_PERIOD[params.grade][params.period];
    // fixed rounding should be applied for new results only (Jira: LDOE-2022)
    const scorePrecision = params.schoolYear >= '2025' ? 3 : undefined;
    const round = value => new Decimal(value).toDecimalPlaces(scorePrecision).round().toNumber();
    const rawScore = (round((totalWeightedScore - mean) / standardDeviation * COMPOSITE_SCORE_MULTIPLIER)
                      + scoreOffsetConstant);
    return params.scoresType === SECURE_SCORE_TYPE ? getEquatedScore(rawScore, params) : rawScore;
}

/**
 * Return object with interpreted composite score based on the calculation formula.
 *
 * @param {object} params - Properties:
 * grade - string - student's grade;
 * period - string - Assessment time of year (BOY|MOY|EOY|SUMMER);
 * scoresType - string - type of the measure scores (regular | secure)
 * @param {object} measureScores - The object with student's measure scores.
 * @returns {object} Object with compositeScore num value and its string interpretation.
 */
function getCompositeScoreObj(params, measureScores) {
    const compositeScore = getCompositeScoreValue(params, measureScores);
    return {
        compositeScore,
        isEquated: params.scoresType === SECURE_SCORE_TYPE,
        interpretation: getCompositeScoreInterpretation(params.grade, params.period, compositeScore),
    };
}

/**
 * Calculate DIBELS8 composite score for a window(grade+period) from student's measures scores.
 * Use gated measure scores if results contain gating measure result and it meets the gating criteria.
 *
 * If not all required measures provided - returns null composite score with 'Paused, No Interpretation' interpretation.
 *
 * @param {object} params - Properties:
 * grade - string - student's grade;
 * period - string - Assessment time of year (BOY|MOY|EOY|SUMMER);
 * scoresType - string - type of the measure scores (regular | secure)
 * @param {object} scoresInfo - The object with student's measure scores.
 * @param {Boolean} includeGatedScores- return gated measure scores
 * Example: { ORF: { rawScore: 12 }, 'ORF-ACC': { rawScore: 32 }, WRF: { rawScore: 32 }, ...}
 * @param {boolean} includeGatedScores - flag to apply gating rules
 * @returns {object} Object with compositeScore num value and its string interpretation.
 */
function calculateCompositeScore(params, scoresInfo, includeGatedScores = false) {
    let studentMeasureScores = cloneDeep(scoresInfo);
    let gatedMeasureScores = {};
    if (isGatingProcedureMet(params.grade, params.period, scoresInfo) && includeGatedScores) {
        gatedMeasureScores = getGatedMeasureScores(params.grade, params.period, scoresInfo);
        studentMeasureScores = Object.entries(gatedMeasureScores)
            .reduce((allMeasureScores, [gatedMeasure, gatedMeasureObj]) => ({
                ...allMeasureScores,
                [gatedMeasure]: { rawScore: gatedMeasureObj.imputedScore },
            }), studentMeasureScores);
    }

    const requiredMeasures = Object.keys(MEASURE_WEIGHTS_BY_GRADE[params.grade]);
    const hasAllRequiredMeasures = requiredMeasures.every(
        measureName => Object.keys(studentMeasureScores).includes(measureName),
    );
    return {
        ...(includeGatedScores) && { gatedMeasuresStatus: gatedMeasureScores },
        overallStatus: (hasAllRequiredMeasures)
            ? getCompositeScoreObj(params, studentMeasureScores)
            : COMPOSITE_SCORE_WITH_PAUSED_INTERP,
    };
}


/**
 * Calculates DIBELS8 composite scores for student's results array grouping them by grade and period.
 * Use gated measure scores if results contain gating measure result and it meets the gating criteria.
 *
 * Throws an Error if studentResults is not an Array.
 * Invalid results with is_valid=false are logged and excluded from calculations.
 *
 * @param {Array} studentResults - List of results objects of mixed grades/periods for a student.
 * @param {boolean} includeGatedScores - return gated measure scores
 * @param {Array} windowsWithDisabledGatingRules - list of windows for which gating rules are skipped
 * @param {string} schoolYear - Required for equated composite score only. If not provided try to get value from
 * studentResults
 * @returns {object} Composite scores with interpretations grouped by grade and period.
 */
function calculateCompositeScoresForStudent(
    studentResults,
    includeGatedScores = false,
    windowsWithDisabledGatingRules = [],
    schoolYear = studentResults[0]?.schoolYear,
) {
    const scoresInfoByWindow = getScoresInfoByWindow(studentResults);
    const compositeScoresByWindow = {};
    Object.keys(scoresInfoByWindow).forEach((grade) => {
        compositeScoresByWindow[grade] = {};
        Object.keys(scoresInfoByWindow[grade]).forEach((period) => {
            const scoresType = SCORE_TYPE_PRECEDENCE.find(
                scoreType => scoresInfoByWindow[grade][period][scoreType] !== undefined,
            );
            const scores = scoresInfoByWindow[grade][period][scoresType];
            const params = {
                schoolYear, grade, period, scoresType,
            };
            const gatedScoresIncluded = (includeGatedScores
                                                  && !windowsWithDisabledGatingRules.includes(`${grade}_${period}`));
            compositeScoresByWindow[grade][period] = calculateCompositeScore(params, scores, gatedScoresIncluded);
        });
    });
    return compositeScoresByWindow;
}

/**
 * Calculate DIBELS8 composite score for a window(grade+period) from student's goals.
 *
 * @param {string} grade - student's grade
 * @param {string} period - Assessment time of year (BOY|MOY|EOY|SUMMER)
 * @param {object} studentGoals - The object with student's goals.
 * Example: { ORF: 12, 'ORF-ACC': 32, WRF: 32, ...}
 * @returns {object} Object with compositeScore num value and its string interpretation.
 */
function calculateCompositeScoreForStudentGoals(grade, period, studentGoals) {
    const studentMeasureScores = Object.entries(studentGoals)
        .reduce((measureScores, [measure, goal]) => ({
            ...measureScores,
            [measure]: { rawScore: goal },
        }), {});

    const requiredMeasures = Object.keys(MEASURE_WEIGHTS_BY_GRADE[grade]);
    const hasAllRequiredMeasures = requiredMeasures.every(
        measureName => Object.keys(studentGoals).includes(measureName),
    );
    const params = {
        // schoolYear is set as 2025 to always use fixed rounding for student goals
        grade, period, schoolYear: '2025', scoresType: REGULAR_SCORE_TYPE,
    };
    return {
        overallStatus: (hasAllRequiredMeasures)
            ? getCompositeScoreObj(params, studentMeasureScores)
            : COMPOSITE_SCORE_WITH_PAUSED_INTERP,
    };
}

module.exports = {
    calculateCompositeScore,
    calculateCompositeScoresForStudent,
    calculateCompositeScoreForStudentGoals,
    getRawCompositeCutscores,
};
